import { WebStorage } from './WebStorage'
import { FileSystemStorage } from './FileSystemStorage'
import { ElectronStorage } from './ElectronStorage'
import { cacheManager, CacheManager } from './CacheManager'
import { vaultStore } from '$lib/stores/vaultStore'
import { get } from 'svelte/store'
import type { IStorage } from './StorageInterface'
import type { Document, DocumentInfo, SearchResult, StorageOptions } from './types'

/**
 * Storage Manager - Platform detection and storage orchestration
 * Single Responsibility: Manage storage strategy based on platform
 */
export class StorageManager implements IStorage {
  private storage: IStorage
  private cache: CacheManager
  private autoSaveTimers: Map<string, NodeJS.Timeout> = new Map()
  private options: StorageOptions
  private lastVaultPath: string | null = null
  
  constructor(options: StorageOptions = {}) {
    this.options = {
      autoSave: true,
      autoSaveInterval: 30000, // 30 seconds
      ...options
    }
    
    // Initialize cache
    this.cache = cacheManager
    
    // Detect platform and initialize appropriate storage
    this.storage = this.detectAndInitStorage()
  }
  
  /**
   * Detect platform and return appropriate storage implementation
   */
  private detectAndInitStorage(): IStorage {
    // Check if we're in Electron environment first
    const isElectron = typeof window !== 'undefined' && 
                       ((window as any).api || 
                        (window as any).electronAPI || 
                        navigator.userAgent.toLowerCase().includes('electron'))
    
    if (isElectron) {
      // console.log('Electron environment detected')
      const vault = get(vaultStore)
      if (vault.isInitialized && vault.vaultHandle) {
        // console.log('Using FileSystemStorage in Electron with vault')
        // In Electron with vault, use FileSystemStorage with IPC backend
        return new FileSystemStorage(this.options)
      } else {
        // console.log('No vault in Electron, using WebStorage')
        return new WebStorage(this.options)
      }
    }
    
    // Check if vault is selected - use FileSystemStorage if so (for web)
    const vault = get(vaultStore)
    if (vault.isInitialized && vault.vaultHandle) {
      console.log('Vault initialized in web, using FileSystemStorage')
      return new FileSystemStorage(this.options)
    }
    
    // Check if we're in Tauri environment
    if (typeof window !== 'undefined' && (window as any).__TAURI__) {
      // TODO: Return DesktopStorage when implemented
      console.log('Tauri environment detected, using WebStorage for now')
      return new WebStorage(this.options)
    }
    
    // Check if we're in a web environment
    if (typeof window !== 'undefined' && typeof indexedDB !== 'undefined') {
      console.log('Web environment detected, using IndexedDB')
      return new WebStorage(this.options)
    }
    
    // Fallback to WebStorage
    console.log('Using WebStorage as default')
    return new WebStorage(this.options)
  }
  
  /**
   * Initialize storage
   */
  async init(): Promise<void> {
    if (this.storage instanceof WebStorage) {
      await (this.storage as WebStorage).init()
    } else if (this.storage instanceof FileSystemStorage) {
      await (this.storage as FileSystemStorage).init()
    } else if (this.storage instanceof ElectronStorage) {
      await (this.storage as ElectronStorage).init()
    }
  }
  
  /**
   * Reinitialize storage when vault changes.
   * Cache is preserved when the vault path is the same (same-vault reinitialize).
   */
  async reinitialize(): Promise<void> {
    // Cancel all auto-save timers
    this.autoSaveTimers.forEach(timer => clearTimeout(timer))
    this.autoSaveTimers.clear()

    // Snapshot the current vault path before re-detecting storage
    const currentVaultPath = get(vaultStore).vaultPath ?? null

    // Only clear cache when the vault has actually changed
    if (this.lastVaultPath !== currentVaultPath) {
      this.cache.clear()
    }

    // Update tracked vault path and reinitialize storage
    this.lastVaultPath = currentVaultPath
    this.storage = this.detectAndInitStorage()
    await this.init()
  }

  /**
   * Initialize demo mode with in-memory storage
   */
  async initializeDemo(): Promise<void> {
    // Cancel all auto-save timers
    this.autoSaveTimers.forEach(timer => clearTimeout(timer))
    this.autoSaveTimers.clear()

    // Clear cache
    this.cache.clear()

    // Use WebStorage for demo mode (in-memory)
    this.storage = new WebStorage(this.options)
    await this.init()

    // Clear any existing documents in IndexedDB for clean demo
    if (this.storage.deleteAll) {
      await this.storage.deleteAll()
    }
  }

  /**
   * Save document with caching and auto-save
   */
  async save(id: string, content: string, metadata?: Partial<Document['metadata']>): Promise<void> {
    // Save to storage
    await this.storage.save(id, content, metadata)
    
    // Update cache
    const document = await this.storage.load(id)
    this.cache.set(id, document)
    
    // Cancel any existing auto-save timer for this document
    this.cancelAutoSave(id)
  }
  
  /**
   * Save with auto-save debouncing
   */
  async saveWithAutoSave(id: string, content: string, metadata?: Partial<Document['metadata']>): Promise<void> {
    // Cancel existing timer
    this.cancelAutoSave(id)
    
    // If auto-save is enabled, set a new timer
    if (this.options.autoSave) {
      const timer = setTimeout(async () => {
        try {
          await this.save(id, content, metadata)
          console.log(`Auto-saved document ${id}`)
        } catch (error) {
          console.error(`Failed to auto-save document ${id}:`, error)
        }
      }, this.options.autoSaveInterval)
      
      this.autoSaveTimers.set(id, timer)
    }
  }
  
  /**
   * Load document with caching
   */
  async load(id: string, force: boolean = false): Promise<Document> {
    // Skip cache if force reload requested
    if (!force) {
      // Check cache first
      const cached = this.cache.get(id)
      if (cached) {
        this.cache.recordHit()
        return cached
      }
    }
    
    this.cache.recordMiss()
    
    // Load from storage (pass force parameter)
    const document = await this.storage.load(id, force)
    
    // Update cache with fresh content
    this.cache.set(id, document)
    
    return document
  }
  
  /**
   * Delete document
   */
  async delete(id: string): Promise<void> {
    // Cancel any auto-save timer
    this.cancelAutoSave(id)

    // Remove from cache
    this.cache.remove(id)

    // Delete from storage
    await this.storage.delete(id)
  }

  /**
   * Rename a document (update cache and storage)
   */
  rename(oldId: string, newId: string, newTitle?: string): void {
    // Cancel any auto-save timer for old id
    this.cancelAutoSave(oldId)

    // Get cached document if exists
    const cachedDoc = this.cache.get(oldId)
    if (cachedDoc) {
      // Update document in cache
      cachedDoc.id = newId
      if (newTitle) {
        cachedDoc.title = newTitle
      }
      // Remove old cache entry
      this.cache.remove(oldId)
      // Add new cache entry
      this.cache.set(newId, cachedDoc)
    }

    // Update storage if it has rename method
    if (typeof (this.storage as any).rename === 'function') {
      (this.storage as any).rename(oldId, newId, newTitle)
    }
  }

  /**
   * List all documents with caching
   */
  async list(): Promise<DocumentInfo[]> {
    // Always fetch fresh data from storage - don't use cache for list
    // Cache can be stale after file deletions/renames
    const infos = await this.storage.list()
    
    // Update cache with fresh data
    infos.forEach(info => {
      this.cache.setInfo(info.id, info)
    })
    
    // Also clean up cache - remove items that no longer exist
    const validIds = new Set(infos.map(info => info.id))
    const cachedIds = this.cache.getAllInfos().map(info => info.id)
    cachedIds.forEach(id => {
      if (!validIds.has(id)) {
        this.cache.remove(id)
      }
    })
    
    return infos
  }
  
  /**
   * Search documents
   */
  async search(query: string): Promise<SearchResult[]> {
    return await this.storage.search(query)
  }
  
  /**
   * Search by tags
   */
  async searchByTags(tags: string[]): Promise<DocumentInfo[]> {
    if (this.storage.searchByTags) {
      return await this.storage.searchByTags(tags)
    }
    
    // Fallback implementation
    const allDocs = await this.list()
    return allDocs.filter(doc => 
      doc.tags && tags.some(tag => doc.tags?.includes(tag))
    )
  }
  
  /**
   * Check if document exists
   */
  async exists(id: string): Promise<boolean> {
    // Check cache first
    if (this.cache.has(id)) {
      return true
    }
    
    return await this.storage.exists(id)
  }
  
  /**
   * Get document info
   */
  async getInfo(id: string): Promise<DocumentInfo | null> {
    // Check cache first
    const cached = this.cache.getInfo(id)
    if (cached) {
      return cached
    }
    
    const info = await this.storage.getInfo(id)
    
    // Cache the result
    if (info) {
      this.cache.setInfo(id, info)
    }
    
    return info
  }
  
  /**
   * Delete all documents
   */
  async deleteAll(): Promise<void> {
    // Cancel all auto-save timers
    this.autoSaveTimers.forEach(timer => clearTimeout(timer))
    this.autoSaveTimers.clear()
    
    // Clear cache
    this.cache.clear()
    
    // Delete from storage
    if (this.storage.deleteAll) {
      await this.storage.deleteAll()
    }
  }
  
  /**
   * Export all documents
   */
  async exportAll(): Promise<Document[]> {
    if (this.storage.exportAll) {
      return await this.storage.exportAll()
    }
    
    // Fallback implementation
    const infos = await this.list()
    const documents: Document[] = []
    
    for (const info of infos) {
      const doc = await this.load(info.id)
      documents.push(doc)
    }
    
    return documents
  }
  
  /**
   * Import documents
   */
  async importDocuments(documents: Document[]): Promise<void> {
    if (this.storage.importDocuments) {
      await this.storage.importDocuments(documents)
      
      // Update cache
      documents.forEach(doc => {
        this.cache.set(doc.id, doc)
      })
    } else {
      // Fallback implementation
      for (const doc of documents) {
        await this.save(doc.id, doc.content, doc.metadata)
      }
    }
  }
  
  /**
   * Create a new document
   * @param title - Document title
   * @param content - Initial content
   * @param folderPath - Optional folder path to create the document in
   */
  async createDocument(title?: string, content?: string, folderPath?: string): Promise<string> {
    // Get vault store for path information
    const { get } = await import('svelte/store')
    const vaultModule = await import('$lib/stores/vaultStore')
    const vault = get(vaultModule.vaultStore)
    
    // Build the base path including folder
    // Don't add vault path here - FileSystemStorage will handle it
    let basePath = ''
    
    // Handle folderPath - it should be relative to vault path
    if (folderPath) {
      // Check if folderPath is absolute and already contains vault path
      if (vault?.vaultPath && folderPath.startsWith(vault.vaultPath)) {
        // It's an absolute path, extract the relative part
        const relativePath = folderPath.substring(vault.vaultPath.length).replace(/^\//, '')
        if (relativePath) {
          basePath += relativePath + '/'
        }
      } else {
        // It's already relative, just append it
        basePath += folderPath + '/'
      }
    }
    
    // Use the title as the base for the filename
    const baseTitle = title || 'Untitled'
    
    // Sanitize title for filename (keep spaces, only remove invalid characters)
    const sanitizedTitle = baseTitle
      .replace(/[<>:"/\\|?*]/g, '') // Remove invalid characters
      .replace(/[^\w\s\-\_\u4e00-\u9fa5\uac00-\ud7af]/g, '') // Keep alphanumeric, spaces, Chinese, Korean chars
      .trim() // Remove leading/trailing spaces
      .slice(0, 100) // Limit length
    
    // Use sanitized title if available, otherwise use base title
    const baseFilename = sanitizedTitle || baseTitle
    
    // Skip duplicate checking since fileStore already handles it via generateUniqueTitle
    // Just use the provided title directly
    let filename = baseFilename
    let id = basePath + filename + '.md'
    
    // Use the provided title as-is (fileStore already made it unique)
    let displayTitle = title || baseTitle
    
    // Don't create a default template - start with empty content
    const defaultContent = content || ''
    
    await this.save(id, defaultContent, {
      title: displayTitle, // Use display-friendly title
      tags: []
    })
    
    return id
  }
  
  /**
   * Get cache statistics
   */
  getCacheStats() {
    return this.cache.getStats()
  }
  
  // Private methods
  
  private generateId(): string {
    return `doc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }
  
  private cancelAutoSave(id: string): void {
    const timer = this.autoSaveTimers.get(id)
    if (timer) {
      clearTimeout(timer)
      this.autoSaveTimers.delete(id)
    }
  }
  
  /**
   * Cleanup resources
   */
  destroy(): void {
    // Cancel all auto-save timers
    this.autoSaveTimers.forEach(timer => clearTimeout(timer))
    this.autoSaveTimers.clear()
    
    // Clear cache
    this.cache.clear()
  }
}

// Singleton instance
let storageManager: StorageManager | null = null

export function getStorageManager(options?: StorageOptions): StorageManager {
  if (!storageManager) {
    storageManager = new StorageManager(options)
  }
  return storageManager
}

// Initialize on import
export const storage = getStorageManager()